import { SearchOutlined, SettingOutlined } from "@ant-design/icons"
import { Button, Card, Flex, Typography } from "antd"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useParams } from "react-router"

import { SearchBar } from "../../../../../../components/Input/SearchBar.tsx"
import { nextPageUrl } from "../../../../../../redux/shared/paged-list.ts"
import type { RootState } from "../../../../../../store.ts"
import { extractParamsFromNextUrl } from "../../../../../../utils/form/extract-params-from-next-url.ts"
import { serializeData } from "../../../../../../utils/utils"
import { fetchProjectSubcontractors } from "../../../../redux/project-subcontractors/project-subcontractors.thunks.ts"
import { projectSetProp } from "../../../../redux/project.slice.ts"
import type { ProjectSubcontractorFilters } from "../../../../redux/project.types.ts"
import { type ProjectSubcontractor } from "../../../../types/project-subcontractors.types.tsx"
import { ProjectViewSubcontractorsList } from "./ProjectViewSubcontractors/ProjectViewSubcontractorsList.tsx"

export function ProjectViewSubcontractorsSection({
  fetching,
  filters,
  data,
  selectedSubcontractor,
  getSubcontractors,
  setProp
}: ConnectedProps<typeof connector>) {
  const { t } = useTranslation()
  const scrollWrapRef = useRef<HTMLDivElement>(null)
  const [configOpen, setConfigOpen] = useState(false)

  const projectId = useParams()?.id as string
  const subcontractors = data?.results || []

  const setFilters = useCallback(
    (patch: Partial<ProjectSubcontractorFilters>) => {
      setProp({
        key: "subcontractorsFilters",
        value: { ...filters, ...patch }
      })
    },
    [setProp, filters]
  )

  const handleFilterChange = useCallback(
    (patch: Partial<ProjectSubcontractorFilters>) => {
      setFilters({ ...patch, page: 1 })
    },
    [setFilters]
  )

  const handleOrderingChange = useCallback(
    (ordering: string | null) => {
      setFilters({ ordering, page: 1 })
    },
    [setFilters]
  )

  const handleSubcontractorSelected = useCallback(
    (item: ProjectSubcontractor | null) => {
      setProp({ key: "selectedSubcontractor", value: item })
    },
    [setProp]
  )

  const handleGetSubcontractors = useCallback(() => {
    const serializeFilters = serializeData(
      filters || {}
    ) as ProjectSubcontractorFilters

    scrollWrapRef.current
      ?.querySelector<HTMLElement>(".ant-table-body, .ant-table-content")
      ?.scrollTo({ top: 0 })

    getSubcontractors({
      isMore: false,
      params: { ...serializeFilters, project_id: projectId }
    }).unwrap()
  }, [getSubcontractors, filters, projectId])

  const nextUrl = nextPageUrl(data)
  const handleGetMoreSubcontractors = useCallback(async () => {
    if (!nextUrl) return

    const serializeFilters =
      extractParamsFromNextUrl<ProjectSubcontractorFilters>(nextUrl)

    await getSubcontractors({
      isMore: true,
      params: { project_id: projectId, ...serializeFilters }
    })
  }, [nextUrl, getSubcontractors, projectId])

  useEffect(() => {
    void handleGetSubcontractors()
  }, [handleGetSubcontractors])

  return (
    <Card
      classNames={{ body: "p-0", header: "p-2" }}
      title={
        <Flex vertical gap={8}>
          <Flex justify="space-between" align="center" gap={8}>
            <Typography.Title level={4} className="m-0">
              {t("tabbedForm.sections.financialSummary")}
            </Typography.Title>
            <Button
              size="small"
              icon={<SettingOutlined />}
              onClick={() => setConfigOpen(true)}
            >
              {t("entityTable.customize")}
            </Button>
          </Flex>
          <Flex gap={8} align="center" justify="flex-end" wrap="wrap">
            <SearchBar
              className={"md:w-1/3 flex-none"}
              placeholder="Search by name..."
              defaultValue={filters?.q || ""}
              prefix={<SearchOutlined />}
              onSearch={(q) => handleFilterChange({ q })}
            />
          </Flex>
        </Flex>
      }
    >
      <div ref={scrollWrapRef}>
        <ProjectViewSubcontractorsList
          selectedId={selectedSubcontractor?.id}
          fetching={fetching}
          subcontractors={subcontractors}
          next={data?.next}
          retryNext={data?.retryNext}
          count={data?.count || 0}
          configControl={{ open: configOpen, onOpenChange: setConfigOpen }}
          onOrderingChange={handleOrderingChange}
          onLoadMore={handleGetMoreSubcontractors}
          onItemSelected={handleSubcontractorSelected}
        />
      </div>
    </Card>
  )
}

const mapStateToProps = (state: RootState) => ({
  filters: state.project.subcontractorsFilters,
  fetching: state.project.fetchingSubcontractors,
  data: state.project.subcontractors,
  selectedSubcontractor: state?.project?.selectedSubcontractor
})

const mapDispatchToProps = {
  getSubcontractors: fetchProjectSubcontractors,
  setProp: projectSetProp
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProjectViewSubcontractorsSection)
